import { useRef, useState } from "react";
import styled from "@emotion/styled";
import { Mentions } from "antd5";

import Button from "components/ui/button/Button";
import Divider from "components/ui/divider/Divider";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { convertTextToMarkdown, getDisplayUserName } from "lib/comment_utils";
import { useCreateComment } from "lib/hooks/api/comments/useCreateComment";
import { useDeleteComment } from "lib/hooks/api/comments/useDeleteComment";
import { useUpdateComment } from "lib/hooks/api/comments/useUpdateComment";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUsers } from "lib/hooks/api/useUsers";
import { BasicComment } from "../../lib/generated/app-service-gql/graphql";
import { CommentObjectType } from "../../lib/types/graphQLEnums";
import { Flex, Text } from "../../styles/utility-components";
import { toast } from "../ui/toast/Toast";
import { CommentComponent, CommentDto } from "./CommentComponent";

type Props = {
  objectId: string;
  objectType: CommentObjectType;
  objectName: string;
  comments: CommentDto[];
  onCreateComment: (newComment: BasicComment) => void;
  onDeleteComment: (deletedCommentId: string) => void;
  onUpdateComment: (updatedComment: BasicComment) => void;
  /** Laid out in the page's flow rather than in a card: no padding of its own, a page-level heading. */
  inline?: boolean;
};

export default function CommentsSidebar({
  inline = false,
  comments,
  objectId,
  objectType,
  objectName,
  onCreateComment,
  onDeleteComment,
  onUpdateComment,
}: Props) {
  const { data: users } = useUsers();
  const user = useAssertCurrentUser();

  const userOptions = users?.map((user) => ({
    value: getDisplayUserName(user),
    label: getDisplayUserName(user),
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const [newComment, setNewComment] = useState("");
  const [editCommentId, setEditCommentId] = useState("");
  const [editedComment, setEditedComment] = useState("");

  const { mutate: createComment } = useCreateComment({
    onSuccess: (data) => {
      onCreateComment(data.createComment);
      toast.success("Comment added successfully");
      setNewComment("");
    },
    onError: () => {
      toast.error("Failed to add comment");
    },
  });

  const { mutate: deleteComment } = useDeleteComment({
    onSuccess: (data) => {
      onDeleteComment(data.deleteComment.id);
      toast.success("Comment deleted successfully");
    },
  });

  const { mutate: updateComment } = useUpdateComment({
    onSuccess: (data) => {
      onUpdateComment(data.updateComment);
      toast.success("Comment updated successfully");
    },
  });

  const handleSubmit = () => {
    const newCommentWithFormattedMentions = convertTextToMarkdown(newComment, users);

    createComment({
      request: {
        content: newCommentWithFormattedMentions,
        objectId,
        objectType,
        objectName,
      },
    });
    setNewComment("");
  };

  const handleDelete = (id: string) => {
    deleteComment({ request: { id } });
  };

  const enabledEdit = (id: string) => {
    setEditCommentId(id);
    setEditedComment(comments.find((comment) => comment.id === id)?.content || "");
  };

  const handleUpdate = (id: string) => {
    const editedCommentWithFormattedMentions = convertTextToMarkdown(editedComment, users);

    updateComment({ request: { id, content: editedCommentWithFormattedMentions, objectName } });
    setEditCommentId("");
    setEditedComment("");
  };

  return (
    <Flex height="100%" flexDirection="column">
      <Header
        inline={inline}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && newComment.trim().length > 0) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <Text h2={inline} h3={!inline}>
          Comments
        </Text>
        <Flex aria-label="Comments actions" gap={8} flexDirection="column">
          <div ref={containerRef}>
            <Mentions
              autoSize={{ minRows: 2 }}
              options={userOptions}
              value={newComment}
              onChange={(e) => setNewComment(e)}
              placeholder="Add a comment"
              getPopupContainer={() => containerRef.current ?? document.body}
            />
          </div>
          <Flex gap={4} justifyContent="flex-end">
            <TooltipComponent
              content="Submit comment"
              keyboardShortcut={{ ctrlKey: true, keys: ["↵"], macOsRemapKeys: true }}
            >
              <Button
                buttonType="primary"
                icon="arrowCircleDown"
                disabled={!newComment.trim()}
                onClick={handleSubmit}
                aria-label="Submit comment"
              />
            </TooltipComponent>
          </Flex>
        </Flex>
      </Header>
      <Divider />
      <CommentsList inline={inline}>
        {comments.length === 0 && (
          <EmptyCommentsMessage justifyContent="center" alignItems="center">
            <Text color="sysTextSecondary">No comments yet.</Text>
          </EmptyCommentsMessage>
        )}
        {comments.map((comment) => (
          <CommentComponent
            key={comment.id}
            comment={comment}
            user={user}
            enabledEdit={enabledEdit}
            handleDelete={handleDelete}
            editCommentId={editCommentId}
            editedComment={editedComment}
            setEditedComment={setEditedComment}
            handleUpdate={handleUpdate}
            setEditCommentId={setEditCommentId}
          />
        ))}
      </CommentsList>
    </Flex>
  );
}

const Header = styled(Flex, { shouldForwardProp: (prop) => prop !== "inline" })<{
  inline: boolean;
}>(({ inline }) => ({
  padding: inline ? 0 : 24,
  gap: 16,
  flexDirection: "column",
}));

const EmptyCommentsMessage = styled(Flex)({
  padding: "32px 0",
});

const CommentsList = styled(Flex, { shouldForwardProp: (prop) => prop !== "inline" })<{
  inline: boolean;
}>(({ inline }) => ({
  flex: 1,
  overflowY: "auto",
  padding: inline ? "24px 0 0" : 24,
  flexDirection: "column",
  gap: 24,
}));
